import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AuthService } from '../../modules/auth/services/auth.service';
import {
  CG_FORCED_THEME,
  CG_THEME_ASSETS,
  CG_THEME_STORAGE_KEY,
  CgThemeAssets,
  CgThemeToken,
  normalizeThemeName,
} from './theme.registry';

/**
 * Application bazli tema yonetimi. Tema adi normalde API'den currentUser.themeName
 * ile gelir (Login/UserRefreshData); <html data-cg-theme> attribute'u uzerinden
 * cg-themes.scss'teki --cg-* degiskenlerini secer.
 *
 * On-prem kurulumlarda (CG_FORCED_THEME dolu, bkz. theme.registry.ts) tema
 * build-time sabittir: API currentUser.themeName ne donerse donsun tamamen
 * yoksayilir, kullanici/oturum degisikliginde de degismez.
 *
 * Attribute <body>'ye DEGIL <html>'e yazilir: LayoutInitService her navigasyonda
 * body class/attribute'larini temizliyor. Cross-tab senkron AuthService'in
 * storage listener'i uzerinden gelir (currentUser degisince yeniden emit edilir).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private themeSubject: BehaviorSubject<CgThemeToken>;
  theme$: Observable<CgThemeToken>;
  assets$: Observable<CgThemeAssets>;

  constructor(private authService: AuthService) {
    // Sabit temali build: theme-init.js/main.ts zaten bu degeri pre-boot
    // yazmis olur; API/localStorage'dan gelebilecek farkli bir deger asla
    // dikkate alinmaz.
    const initial = CG_FORCED_THEME
      ? CG_FORCED_THEME
      : normalizeThemeName(document.documentElement.getAttribute('data-cg-theme'));
    this.themeSubject = new BehaviorSubject<CgThemeToken>(initial);
    this.theme$ = this.themeSubject.asObservable().pipe(distinctUntilChanged());
    this.assets$ = this.theme$.pipe(map((t) => CG_THEME_ASSETS[t]));
    if (CG_FORCED_THEME) {
      this.apply(CG_FORCED_THEME);
    }
  }

  get current(): CgThemeToken {
    return this.themeSubject.value;
  }

  get assets(): CgThemeAssets {
    return CG_THEME_ASSETS[this.current];
  }

  init(): void {
    if (CG_FORCED_THEME) {
      // Bu build icin tema environment'tan sabitlenmis; API'nin
      // currentUser.themeName degerini dinlemeye gerek yok.
      return;
    }
    this.authService.currentUser$.subscribe((user) => {
      if (!user) {
        // Logout localStorage.clear() cagiriyor; login ekraninin son bilinen
        // temayi koruyabilmesi icin cg_theme geri yazilir.
        this.persist(this.current);
        return;
      }
      const themeName = (user as { themeName?: string }).themeName;
      if (!themeName) {
        // Eski cache'lenmis oturum / SSO'da elle kurulan UserModel'de themeName
        // olmayabilir; yanlis temaya donmemek icin refreshUserData'yi bekle.
        return;
      }
      this.apply(normalizeThemeName(themeName));
    });
  }

  private apply(token: CgThemeToken): void {
    document.documentElement.setAttribute('data-cg-theme', token);
    this.applyFavicon(token);
    this.persist(token);
    if (this.themeSubject.value !== token) {
      this.themeSubject.next(token);
    }
  }

  // theme-init.js sayfa acilirken (son bilinen tema) ayni favicon'u onceden
  // uygular; burasi login sonrasi tema API'den degisirse sayfa yenilenmeden
  // favicon'un anlik senkron kalmasini saglar.
  private applyFavicon(token: CgThemeToken): void {
    const link = document.getElementById('cg-favicon') as HTMLLinkElement | null;
    if (!link) {
      return;
    }
    const { favicon, faviconType } = CG_THEME_ASSETS[token];
    link.href = favicon;
    link.type = faviconType;
  }

  private persist(token: CgThemeToken): void {
    try {
      localStorage.setItem(CG_THEME_STORAGE_KEY, token);
    } catch {
      // storage erisilemezse sessiz gec (private mod vb.)
    }
  }
}
